import React, { useCallback, useEffect, useState } from 'react';
import Skeleton from '../common/Skeleton';
import Badge from '../common/Badge';
import FormControl from '../common/FormControl';
import Pagination from '../common/Pagination';
import Toast, { ToastMessage } from '../common/Toast';
import NoDataFound from '../common/NoDataFound';
import Button from '../common/Button';
import { Plus, Search, AlertTriangle, Clock, Lock } from 'lucide-react';
import LitInstanceDetail from './lit-instances/LitInstanceDetail';
import { litInstanceService, type LitInstance } from '../../services/litInstanceService';
import { usePermissions, usePermission } from '../../contexts/PermissionContext';
import { statusVariant } from '../../utils/statusVariant';
import ApprovalDot, { WorkflowState } from '../common/ApprovalDot';
import WorkflowModal from '../common/WorkflowModal';
import StatusSwitch from '../common/StatusSwitch';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';

// resource_kind in the backend WF registry (backend/routes/wf_routes.py).
const WF_KIND = 'com_litigation';

function dueBadge(dateStr: string | null, formatDate: (v: string | null) => string) {
  if (!dateStr) return null;
  const days = Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86_400_000);
  if (days < 0)  return <span className="due-badge due-badge--overdue"><AlertTriangle size={10} /> Overdue</span>;
  if (days <= 7) return <span className="due-badge due-badge--soon"><Clock size={10} /> {days}d</span>;
  return <span className="due-badge due-badge--ok">{formatDate(dateStr)}</span>;
}

const ComplianceLitigations: React.FC = () => {
  const { formatDate } = useEntitySettings();
  const { check } = usePermissions();
  const { allowed: canView, loading: viewLoading } = usePermission('COM_LITIGATIONS', 'view');
  const canLitigation = check('COM_LITIGATIONS', 'view+modify').allowed;
  const [rows,        setRows]        = useState<LitInstance[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [toast,       setToast]       = useState<ToastMessage | null>(null);
  const [search,      setSearch]      = useState('');
  const [statusFilter, setStatusFilter] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');
  const [page,        setPage]        = useState(1);
  const [pageSize,    setPageSize]    = useState(10);
  const [modalOpen,   setModalOpen]   = useState(false);
  const [selected,    setSelected]    = useState<LitInstance | null>(null);
  // Workflow (approval) modal — opened by clicking a row's traffic-light dot.
  const [wfRow,       setWfRow]       = useState<LitInstance | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await litInstanceService.getAll()); }
    catch (e: any) { setToast({ type: 'error', message: e?.message || 'Failed to load' }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (canView) load(); }, [load, canView]);
  useEffect(() => { setPage(1); }, [search, statusFilter, pageSize]);

  const wantActive = statusFilter === 'ACTIVE';
  const filtered = rows.filter(r =>
    (r.is_active ?? 1) !== -1 &&
    ((r.is_active ?? 1) === 1) === wantActive &&
    (
      (r.notice_id || '').toLowerCase().includes(search.toLowerCase()) ||
      (r.act || r.law_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (r.issued_by || '').toLowerCase().includes(search.toLowerCase()) ||
      (r.current_step_label || '').toLowerCase().includes(search.toLowerCase())
    )
  );
  const total     = filtered.length;
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  const openCreate = () => { setSelected(null); setModalOpen(true); };
  const openDetail = (r: LitInstance) => { setSelected(r); setModalOpen(true); };

  if (!viewLoading && !canView) {
    return (
      <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
        <NoDataFound title="Not authorised" message="You do not have access to 'COM_LITIGATIONS'." />
      </div>
    );
  }

  return (
    <div className="netbody-card flex flex-col" style={{ flex: 1, minHeight: 0 }}>

      <div className="netbody-header">
        {/* Status — iOS-style on/off switch (on = active) */}
        <StatusSwitch
          value={statusFilter}
          onChange={next => setStatusFilter(next)}
          noun="litigations"
          testId="SEL_lit_status_filter"
        />
        <div className="flex items-center gap-2 ml-auto">
          <FormControl.Input type="text" placeholder="Search…"
            value={search} onChange={e => setSearch(e.target.value)}
            leadingIcon={<Search size={14} />}
            className="filter-search"
            data-testid="INP_lit_search" />
          {canLitigation && <Button variant="fill" icon={Plus} onClick={openCreate}
            data-testid="BTN_lit_create">
            Create
          </Button>}
        </div>
      </div>

      <div className="netbody-content">
        <div className="scroll-container flex-1 overflow-hidden">
          <div className="table-list">
            <div className="table-list-scroll">
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th table-list-cell--dot" aria-label="Workflow status"></th>
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    <th className="table-list-th">Code</th>
                    <th className="table-list-th">Notice ID</th>
                    <th className="table-list-th">Act / Law</th>
                    <th className="table-list-th">Jurisdiction</th>
                    <th className="table-list-th">Template</th>
                    <th className="table-list-th">Current Step</th>
                    <th className="table-list-th">Next Due</th>
                    <th className="table-list-th">Assigned To</th>
                    <th className="table-list-th table-list-cell--status">Status</th>
                  </tr>
                </thead>
                <tbody aria-busy={loading} aria-live="polite">
                  {loading && <Skeleton.TableRows cols={['dot', '3xs', 'sm', 'md', 'md', 'md', 'sm', 'md', 'sm', 'md', 'pill']} />}
                  {!loading && filtered.length === 0 && (
                    <tr>
                      <td colSpan={11} className="table-list-cell">
                        <NoDataFound
                          title={search ? 'No Matching Records' : 'No Litigations Yet'}
                          message={search ? 'Try a different search.' : 'Click "Create New" to record one.'}
                        />
                      </td>
                    </tr>
                  )}
                  {!loading && paginated.map((r, idx) => (
                    <tr key={r.id} className="table-list-row"
                      onClick={() => openDetail(r)}
                      data-testid={`ROW_lit_${r.id}`}>
                      <td
                        className="table-list-cell table-list-cell--dot"
                        onClick={(e) => { e.stopPropagation(); setWfRow(r); }}
                        title="Workflow — view timeline & act"
                        data-testid={`BTN_lit_row_${r.id}_workflow`}
                      >
                        <ApprovalDot
                          state={(r.workflow_state || 'draft') as WorkflowState}
                          onClick={() => setWfRow(r)}
                          title="Workflow — view timeline & act"
                        />
                      </td>
                      <td className="table-list-cell table-list-cell--sno">
                        {(page - 1) * pageSize + idx + 1}
                      </td>
                      <td className="table-list-cell" data-testid={`TXT_litigations_row_${r.id}_code`}>
                        {r.code ? <span className="cell-code">{r.code}</span> : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="table-list-cell">
                        <span className="table-list-cell-title cell-name-lock">
                          {r.workflow_state === 'approved' && (
                            <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                          )}
                          {r.notice_id}
                        </span>
                        {r.notice_date && <span className="table-list-cell-sub">{formatDate(r.notice_date)}</span>}
                      </td>
                      <td className="table-list-cell table-list-cell--truncate" title={r.law_name || r.act || ''}>
                        {r.law_name || r.act || '—'}
                      </td>
                      <td className="table-list-cell table-list-cell--truncate" title={r.jurisdiction_name || r.jurisdiction_text || ''}>
                        {r.jurisdiction_name || r.jurisdiction_text || '—'}
                      </td>
                      <td className="table-list-cell">
                        {r.is_free_style
                          ? <span className="text-[11px] italic text-gray-400">Free-style</span>
                          : r.proceeding_name
                            ? <span className="text-xs text-indigo-700">{r.proceeding_name}</span>
                            : <span className="text-[11px] text-gray-300">—</span>
                        }
                      </td>
                      <td className="table-list-cell">
                        {r.current_step_label
                          ? <span className="text-xs text-gray-700">{r.current_step_label}</span>
                          : <span className="text-[11px] italic text-gray-300">not started</span>
                        }
                      </td>
                      <td className="table-list-cell">
                        {dueBadge(r.next_due_date || r.due_date, formatDate)}
                      </td>
                      <td className="table-list-cell">
                        {r.assigned_user_name
                          ? <div>
                              <span className="text-xs font-medium text-gray-700">{r.assigned_user_name}</span>
                              {r.assigned_position_name && (
                                <span className="block text-[11px] text-gray-400">{r.assigned_position_name}</span>
                              )}
                            </div>
                          : <span className="text-[11px] text-gray-300">unassigned</span>
                        }
                      </td>
                      <td className="table-list-cell table-list-cell--status">
                        <Badge variant={statusVariant(r.status || 'Open')} size="sm">
                          {r.status || 'Open'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} pageSize={pageSize} totalRecords={total}
              onPageChange={setPage} onPageSizeChange={setPageSize}
              pageSizeOptions={[10, 25, 50, 100]} recordLabel="litigations" />
          </div>
        </div>
      </div>

      <LitInstanceDetail
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={load}
        instance={selected}
        existing={rows}
        mode="grc"
      />

      <WorkflowModal
        isOpen={wfRow !== null}
        onClose={() => setWfRow(null)}
        kind={WF_KIND}
        id={wfRow?.id ?? null}
        onChanged={load}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default ComplianceLitigations;
